// src/services/AuthService.js
import router from '@/router'
import { publicRequest } from "@/api/apiPublic";
import { privateRequest } from "@/api/apiPrivate";
import { setAuthRole } from '@/utils/authState'

export const AuthService = {
    /**
     * ===== REGISTER CUSTOMER =====
     * PUBLIC
     * @param {Object} registerRequestDto
     * @param {string} registerRequestDto.email
     * @param {string} registerRequestDto.password
     * @param {string} registerRequestDto.fullName
     * @returns {Promise<Object>}
     */
    async registerCustomer(registerRequestDto) {
        const data = await publicRequest(
            "/auth/register-customer",
            "post",
            registerRequestDto,
            false
        );
        return data;
    },

    /**
     * ===== REGISTER STAFF =====
     * @param {Object} registerRequestDto
     * @returns {Promise<Object>}
     */
    async registerStaff(registerRequestDto) {
        const data = await privateRequest(
            "/auth/register-staff",
            "post",
            registerRequestDto,
            true
        );
        return data;
    },

    /**
     * ===== LOGIN =====
     * PUBLIC
     * @param {Object} payload
     * @param {string} payload.email
     * @param {string} payload.password
     * @returns {Promise<Object>} auth response (accessToken, role, ...)
     */
    async login(payload) {
        const data = await publicRequest(
            "/auth/login",
            "post",
            payload
        );

        localStorage.setItem('accessToken', data.accessToken)
        setAuthRole(data.role)

        return data
    },

    /**
     * ===== LOGOUT =====
     * PRIVATE
     * Gọi API logout, luôn clear role phía client
     * @returns {Promise<void>}
     */
    async logout() {
        try {
            await privateRequest(
                "/auth/logout",
                "post"
            )
        } catch (e) {
            // bỏ qua 401 khi logout
        } finally {
            setAuthRole(null)
            router.push('/login')
        }
    },

    /**
     * ===== BOOTSTRAP AUTH =====
     * PUBLIC
     * Dùng khi reload app để refresh access token
     * @returns {Promise<boolean>} true nếu refresh thành công
     */
    async bootstrapAuth() {
        try {
            const accessToken = await publicRequest(
                "/auth/refresh",
                "post",
                null,
                false
            )

            localStorage.setItem('accessToken', accessToken)
            localStorage.setItem('isAuthenticated', 'true')

            // role KHÔNG set ở đây
            return true
        } catch {
            localStorage.removeItem('accessToken')
            localStorage.removeItem('isAuthenticated')
            localStorage.removeItem('role')
            return false
        }
    },

    /**
     * ===== CHECK AUTH =====
     * @returns {boolean}
     */
    isAuthenticated() {
        return localStorage.getItem('isAuthenticated') === 'true'
    },

    /**
     * ===== GET ROLE =====
     * @returns {string|null}
     */
    getRole() {
        return localStorage.getItem('role')
    },

    /**
     * ===== FORGOT PASSWORD =====
     * PUBLIC
     * @param {string} email
     * @returns {Promise<Object>}
     */
    async forgotPassword(email) {
        const data = await publicRequest(
            "/auth/forgot-password",
            "post",
            { email },
            false
        );
        return data;
    },

    /**
     * ===== RESET PASSWORD =====
     * PUBLIC
     * @param {Object} resetPasswordRequestDto
     * @param {string} resetPasswordRequestDto.email
     * @param {string} resetPasswordRequestDto.otp
     * @param {string} resetPasswordRequestDto.newPassword
     * @returns {Promise<Object>}
     */
    async resetPassword(resetPasswordRequestDto) {
        const data = await publicRequest(
            "/auth/reset-password",
            "post",
            resetPasswordRequestDto,
            false
        );
        return data;
    },

    /**
     * ===== VERIFY OTP =====
     * PUBLIC
     * @param {Object} data
     * @param {string} data.email
     * @param {string} data.otp
     * @returns {Promise<Object>}
     */
    async verifyOtp(data) {
        const res = await publicRequest(
            "/auth/verify-otp",
            "post",
            data,
            false
        );
        return res;
    },

    /**
     * ===== CHANGE PASSWORD =====
     * PRIVATE
     * @param {Object} changePasswordRequestDto
     * @param {string} changePasswordRequestDto.oldPassword
     * @param {string} changePasswordRequestDto.newPassword
     * @returns {Promise<Object>}
     */
    async changePassword(changePasswordRequestDto) {
        const data = await privateRequest(
            "/auth/change-password",
            "post",
            changePasswordRequestDto,
            true
        );
        return data;
    },
};
